import { onCleanup, onMount } from "solid-js"
import type { Object3D } from "three"
import createSharedCachedFactory from "../../../../rx/memo/createSharedCachedFactory"
import removeItem from "../../../array/remove_item"
import type { Engine } from "../../../core/engine/Engine.type"
import runWithObject3DOwner from "../../../three/fn/runWithObject3DOwner"
import useEnginePropertyLightsBlinkingObjectList from "./useEnginePropertyLightsBlinkingObjectList"

const PERIOD = 2000
const LIGHTS_OFF_AT = 600

const useAddToPropertyLightsBlinkingManager = createSharedCachedFactory((
  engine: Engine,
) => {
  const list : Array<(lightsOn: boolean) => void> = useEnginePropertyLightsBlinkingObjectList(engine)

  let lightsOn = false

  const msFromNowToStartOfPeriod = Date.now() % PERIOD

  const clearables : Array<ReturnType<typeof setTimeout>> = []
  const gcELL = engine.gcELL()

  const c0 = setTimeout(() => {
    const c1 = setInterval(() => {
      lightsOn = true
      for (const fn of list) {
        fn(lightsOn)
      }
    }, PERIOD)
    clearables.push(c1)
  }, msFromNowToStartOfPeriod)
  clearables.push(c0)

  const c2 = setTimeout(() => {
    const c3 = setInterval(() => {
      lightsOn = false
      for (const fn of list) {
        fn(lightsOn)
      }
    }, PERIOD)
    clearables.push(c3)
  }, msFromNowToStartOfPeriod + LIGHTS_OFF_AT)
  clearables.push(c2)

  function cleanupFn() {
    for (const clearable of clearables) {
      clearTimeout(clearable)
    }
  }
  gcELL.push(cleanupFn)

  return (
    obj: Object3D,
    fn: (lightsOn: boolean) => void,
  ) => {
    runWithObject3DOwner(obj, () => {
      onMount(() => {
        list.push(fn)
      })
      onCleanup(() => {
        removeItem(list, fn)
      })
    })
  }
})

export default useAddToPropertyLightsBlinkingManager